import { useState, useEffect } from 'react';
import Layout from '../../components/layout/Layout';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Plus, Edit2, Trash2, Search, Power, PowerOff } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../components/ui/alert-dialog';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

export default function Banks({ user, onLogout }) {
  const [banks, setBanks] = useState([]);
  const [filteredBanks, setFilteredBanks] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(false);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [editingBank, setEditingBank] = useState(null);
  const [deletingBank, setDeletingBank] = useState(null);
  const [formData, setFormData] = useState({ name: '', account_number: '', pancard: '', gst: '', address: '', ifsc: '', swift_code: '', account_holder: '', bank_name: '' });

  useEffect(() => {
    fetchBanks();
  }, []);

  useEffect(() => {
    const filtered = banks.filter(bank =>
      bank.name.toLowerCase().includes(searchTerm.toLowerCase())
    );
    setFilteredBanks(filtered);
  }, [searchTerm, banks]);

  const fetchBanks = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/banks`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!response.ok) throw new Error('Failed to fetch banks');
      const data = await response.json();
      setBanks(data);
      setFilteredBanks(data);
    } catch (error) {
      toast.error('Failed to load banks');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name.trim()) {
      toast.error('Bank name is required');
      return;
    }

    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const url = editingBank
        ? `${API_URL}/api/banks/${editingBank.id}`
        : `${API_URL}/api/banks`;
      
      const response = await fetch(url, {
        method: editingBank ? 'PUT' : 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(formData)
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to save bank');
      }

      toast.success(editingBank ? 'Bank updated successfully' : 'Bank created successfully');
      setIsDialogOpen(false);
      setFormData({ name: '', account_number: '', pancard: '', gst: '', address: '', ifsc: '', swift_code: '', account_holder: '', bank_name: '' });
      setEditingBank(null);
      fetchBanks();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (bank) => {
    setEditingBank(bank);
    setFormData({
      name: bank.name,
      account_number: bank.account_number || '',
      pancard: bank.pancard || '',
      gst: bank.gst || '',
      address: bank.address || '',
      ifsc: bank.ifsc || '',
      swift_code: bank.swift_code || '',
      account_holder: bank.account_holder || '',
      bank_name: bank.bank_name || ''
    });
    setIsDialogOpen(true);
  };

  const handleDelete = async () => {
    if (!deletingBank) return;

    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/banks/${deletingBank.id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });

      if (!response.ok) throw new Error('Failed to delete bank');

      toast.success('Bank deleted successfully');
      setIsDeleteDialogOpen(false);
      setDeletingBank(null);
      fetchBanks();
    } catch (error) {
      toast.error('Failed to delete bank');
    } finally {
      setLoading(false);
    }
  };

  const toggleStatus = async (bank) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/banks/${bank.id}/status`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${token}` }
      });

      if (!response.ok) throw new Error('Failed to update status');

      toast.success(`Bank ${bank.is_active ? 'deactivated' : 'activated'} successfully`);
      fetchBanks();
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const openAddDialog = () => {
    setEditingBank(null);
    setFormData({ name: '', account_number: '', pancard: '', gst: '', address: '', ifsc: '', swift_code: '', account_holder: '', bank_name: '' });
    setIsDialogOpen(true);
  };

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="p-4 md:p-8 lg:p-10 max-w-7xl mx-auto space-y-6" data-testid="banks-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-slate-900 tracking-tight">Banks Management</h1>
            <p className="text-slate-500 mt-1">Manage bank master data</p>
          </div>
          <Button onClick={openAddDialog} className="gap-2" data-testid="add-bank-btn">
            <Plus className="w-4 h-4" />
            Add Bank
          </Button>
        </div>

      {/* Search */}
      <div className="flex items-center gap-2 max-w-md">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-4 h-4" />
          <Input
            placeholder="Search banks..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <p className="text-sm text-blue-600 font-medium">Total Banks</p>
          <p className="text-2xl font-bold text-blue-900">{banks.length}</p>
        </div>
        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
          <p className="text-sm text-green-600 font-medium">Active Banks</p>
          <p className="text-2xl font-bold text-green-900">
            {banks.filter(b => b.is_active).length}
          </p>
        </div>
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-sm text-red-600 font-medium">Inactive Banks</p>
          <p className="text-2xl font-bold text-red-900">
            {banks.filter(b => !b.is_active).length}
          </p>
        </div>
      </div>

      {/* Banks Table */}
      <div className="bg-white rounded-lg border shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-slate-50 border-b">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                  Bank Name
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                  Account Number
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                  Created At
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-slate-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {loading && banks.length === 0 ? (
                <tr>
                  <td colSpan="5" className="px-6 py-8 text-center text-slate-500">
                    Loading...
                  </td>
                </tr>
              ) : filteredBanks.length === 0 ? (
                <tr>
                  <td colSpan="5" className="px-6 py-8 text-center text-slate-500">
                    {searchTerm ? 'No banks found matching your search' : 'No banks added yet'}
                  </td>
                </tr>
              ) : (
                filteredBanks.map((bank) => (
                  <tr key={bank.id} className="hover:bg-slate-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="font-medium text-slate-900">{bank.name}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-slate-600 font-mono">{bank.account_number || '-'}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span
                        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          bank.is_active
                            ? 'bg-green-100 text-green-800'
                            : 'bg-red-100 text-red-800'
                        }`}
                      >
                        {bank.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-500">
                      {new Date(bank.created_at).toLocaleDateString('en-IN', {
                        day: '2-digit',
                        month: 'short',
                        year: 'numeric'
                      })}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex justify-end gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => toggleStatus(bank)}
                          className={bank.is_active ? 'text-orange-600 hover:text-orange-700' : 'text-green-600 hover:text-green-700'}
                          title={bank.is_active ? 'Deactivate' : 'Activate'}
                        >
                          {bank.is_active ? <PowerOff className="w-4 h-4" /> : <Power className="w-4 h-4" />}
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleEdit(bank)}
                          className="text-blue-600 hover:text-blue-700"
                        >
                          <Edit2 className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            setDeletingBank(bank);
                            setIsDeleteDialogOpen(true);
                          }}
                          className="text-red-600 hover:text-red-700"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Add/Edit Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingBank ? 'Edit Bank' : 'Add New Bank'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit}>
            <div className="space-y-4 py-4 max-h-[60vh] overflow-y-auto">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="name">Company/Label Name *</Label>
                  <Input id="name" placeholder="e.g. Zestbrains Pvt Ltd" value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} required autoFocus />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="bank_name">Bank Name</Label>
                  <Input id="bank_name" placeholder="e.g. ICICI Bank" value={formData.bank_name} onChange={(e) => setFormData({ ...formData, bank_name: e.target.value })} />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="account_number">Account Number</Label>
                  <Input id="account_number" placeholder="Enter account number" value={formData.account_number} onChange={(e) => setFormData({ ...formData, account_number: e.target.value })} />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="account_holder">A/c Holder</Label>
                  <Input id="account_holder" placeholder="Account holder name" value={formData.account_holder} onChange={(e) => setFormData({ ...formData, account_holder: e.target.value })} />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="ifsc">IFSC Code</Label>
                  <Input id="ifsc" placeholder="e.g. ICIC0001234" value={formData.ifsc} onChange={(e) => setFormData({ ...formData, ifsc: e.target.value })} />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="swift_code">Swift Code</Label>
                  <Input id="swift_code" placeholder="e.g. ICICINBBXXX" value={formData.swift_code} onChange={(e) => setFormData({ ...formData, swift_code: e.target.value })} />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="pancard">PAN Card</Label>
                  <Input id="pancard" placeholder="e.g. ABCDE1234F" value={formData.pancard} onChange={(e) => setFormData({ ...formData, pancard: e.target.value })} />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="gst">GST Number</Label>
                  <Input id="gst" placeholder="e.g. 24ABCDE1234F1Z5" value={formData.gst} onChange={(e) => setFormData({ ...formData, gst: e.target.value })} />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="address">Address</Label>
                <Input id="address" placeholder="Bank/Company address" value={formData.address} onChange={(e) => setFormData({ ...formData, address: e.target.value })} />
              </div>
            </div>
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setIsDialogOpen(false);
                  setEditingBank(null);
                  setFormData({ name: '', account_number: '', pancard: '', gst: '', address: '', ifsc: '', swift_code: '', account_holder: '', bank_name: '' });
                }}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={loading}>
                {loading ? 'Saving...' : editingBank ? 'Update' : 'Create'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete <strong>{deletingBank?.name}</strong>. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setDeletingBank(null)}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-red-600 hover:bg-red-700"
              disabled={loading}
            >
              {loading ? 'Deleting...' : 'Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
    </Layout>
  );
}
